import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { SkeletonSettingsPage } from "@/components/SkeletonPage";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

const RESY_COLOR = "#E63946";

export function BackToIntegrationLandingResy() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-2 text-zinc-600"
      onClick={() => navigate("/integrations")}
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </button>
  );
}

interface ResyConfig {
  id: number | null;
  siteId: number;
  venueId: string;
  venueName: string;
  defaultPartySize: number;
  bookingWindowDays: number;
  enabled: boolean;
  hasApiKey: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

type TabId = "overview" | "config" | "ai-tools" | "logs";

const AI_TOOLS = [
  {
    name: "resy_check_availability",
    label: "Check availability",
    description: "Find open Resy slots for a given party size and date range."
  },
  {
    name: "resy_book_reservation",
    label: "Book reservation",
    description:
      "Reserve a table with guest name, phone, email, and special notes."
  },
  {
    name: "resy_cancel_reservation",
    label: "Cancel reservation",
    description: "Cancel a Resy reservation by confirmation ID."
  },
  {
    name: "resy_get_reservation",
    label: "Get reservation",
    description: "Look up a reservation and return full details."
  }
];

export default function ResyIntegration() {
  const api = useApi();
  const [tab, setTab] = useState<TabId>("config");
  const [config, setConfig] = useState<ResyConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    apiKey: "",
    venueId: "",
    venueName: "",
    defaultPartySize: 2,
    bookingWindowDays: 7,
    enabled: true
  });

  useEffect(() => {
    api.admin
      .get("Resy/Config")
      .then(async (r) => {
        if (r.ok) {
          const d = await r.json();
          setConfig(d);
          setForm((f) => ({
            ...f,
            venueId: d.venueId ?? "",
            venueName: d.venueName ?? "",
            defaultPartySize: d.defaultPartySize ?? 2,
            bookingWindowDays: d.bookingWindowDays ?? 7,
            enabled: d.enabled ?? true
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await api.admin.post("Resy/Config", form);
      if (r.ok) {
        const d = await r.json();
        setConfig(d);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      /* non-critical */
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.admin.post("Resy/TestConnection", {});
      const d = await r.json();
      setTestResult({
        ok: r.ok,
        message:
          d.message ?? (r.ok ? "Connection successful" : "Connection failed")
      });
    } catch {
      setTestResult({ ok: false, message: "Request failed" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <SkeletonSettingsPage />;

  const tabsJSX = (
    <>
      <IntegrationTab
        active={tab === "overview"}
        onClick={() => setTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={tab === "config"}
        onClick={() => setTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={tab === "ai-tools"}
        onClick={() => setTab("ai-tools")}
      >
        AI Tools
      </IntegrationTab>
      <IntegrationTab active={tab === "logs"} onClick={() => setTab("logs")}>
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader
      title="Resy"
      tabs={tabsJSX}
      headerActions={
        config?.hasApiKey ? (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            Connected
          </span>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {/* Overview */}
        {tab === "overview" && (
          <div className="space-y-6">
            <div className="bg-zinc-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-3">
                What this integration does
              </h2>
              <p className="text-zinc-600 mb-4">
                Connect Resy so your AI bot and IVR outbound campaigns can book
                restaurant reservations by phone — no hold music, no staff
                required.
              </p>
              <ul className="space-y-2 text-zinc-600 text-sm">
                <li>
                  ✓ IVR caller says how many people — bot checks availability
                  and reads out times
                </li>
                <li>
                  ✓ Caller picks a slot, bot books via Resy API and sends SMS
                  confirmation instantly
                </li>
                <li>
                  ✓ Works across IVR outbound, web chat, SMS, and WhatsApp
                </li>
                <li>✓ Supports guest notes and special requests</li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {AI_TOOLS.map((tool) => (
                <div
                  key={tool.name}
                  className="border border-zinc-200 rounded-lg p-4"
                >
                  <div className="font-medium text-zinc-800 text-sm mb-1">
                    {tool.label}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {tool.description}
                  </div>
                  <div className="mt-2 font-mono text-xs text-zinc-400">
                    {tool.name}
                  </div>
                </div>
              ))}
            </div>
            {!config?.hasApiKey && (
              <div className="flex justify-center">
                <button
                  onClick={() => setTab("config")}
                  className="px-6 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: RESY_COLOR }}
                >
                  Connect Resy →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Configuration */}
        {tab === "config" && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                API Key{" "}
                {config?.hasApiKey && (
                  <span className="text-green-600 text-xs">(saved)</span>
                )}
              </label>
              <input
                type="password"
                placeholder={
                  config?.hasApiKey ? "••••••••••••" : "Enter Resy API key"
                }
                value={form.apiKey}
                onChange={(e) =>
                  setForm((f) => ({ ...f, apiKey: e.target.value }))
                }
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-zinc-400 mt-1">
                From the Resy Partner API developer portal
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Venue ID
              </label>
              <input
                type="text"
                placeholder="e.g. 12345"
                value={form.venueId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, venueId: e.target.value }))
                }
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Venue Name
              </label>
              <input
                type="text"
                placeholder="e.g. Carbone"
                value={form.venueName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, venueName: e.target.value }))
                }
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Default Party Size
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={form.defaultPartySize}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      defaultPartySize: Number(e.target.value)
                    }))
                  }
                  className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Availability Window (days)
                </label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={form.bookingWindowDays}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      bookingWindowDays: Number(e.target.value)
                    }))
                  }
                  className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="resy-enabled"
                checked={form.enabled}
                onChange={(e) =>
                  setForm((f) => ({ ...f, enabled: e.target.checked }))
                }
              />
              <label htmlFor="resy-enabled" className="text-sm text-zinc-700">
                Enabled
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60"
                style={{ backgroundColor: RESY_COLOR }}
              >
                {saving ? "Saving…" : saved ? "Saved ✓" : "Save Configuration"}
              </button>
              {config?.hasApiKey && (
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="px-5 py-2 rounded-lg border border-zinc-300 text-sm font-medium text-zinc-700 disabled:opacity-60"
                >
                  {testing ? "Testing…" : "Test Connection"}
                </button>
              )}
            </div>
            {testResult && (
              <div
                className={`text-sm rounded-lg px-4 py-2 ${testResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
              >
                {testResult.message}
              </div>
            )}
          </div>
        )}

        {/* AI Tools */}
        {tab === "ai-tools" && (
          <div className="space-y-3">
            <p className="text-zinc-500 text-sm">
              These tools are automatically available to the AI bot when Resy is
              connected.
            </p>
            {AI_TOOLS.map((tool) => (
              <div
                key={tool.name}
                className="border border-zinc-200 rounded-lg p-4 flex items-start gap-4"
              >
                <div
                  className="w-2 h-2 rounded-full mt-2 shrink-0"
                  style={{ backgroundColor: RESY_COLOR }}
                />
                <div>
                  <div className="font-medium text-zinc-800">{tool.label}</div>
                  <div className="text-sm text-zinc-500 mt-0.5">
                    {tool.description}
                  </div>
                  <div className="font-mono text-xs text-zinc-400 mt-1">
                    {tool.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity Log */}
        {tab === "logs" && <IntegrationActivityLog integrationSlug="Resy" />}
      </div>
    </StickyRouteHeader>
  );
}
